/**
 * DislikesStep.foodFilters.js
 *
 * Pure functions, no React — mirrors LikesStep.foodFilters.js exactly,
 * applied to the Screen 9 "Dislikes" search catalog instead of Screen
 * 8's. Kept as a separate file rather than a shared import for the same
 * reason DislikesStep.constants.js duplicates LikesStep.constants.js's
 * FOOD_SEARCH_ITEMS rather than importing it (avoiding a fragile
 * cross-folder relative import — see that file's header).
 *
 * Do not confuse this with the Dislikes *selection* itself, which is a
 * separate, unrelated hard-exclusion mechanism applied later in the
 * pipeline (a disliked food never appears in the generated plan, same
 * severity tier as an allergen — see DislikesStep.js's header). This
 * module only shapes which items are *suggested* to mark as disliked in
 * the first place: per FSD v2.1 §6 Seven-Day Plan Definition, Diet Type
 * is a hard filter (no point suggesting "Chicken" as a possible dislike
 * to someone who selected Vegetarian on Screen 7 — it can never appear in
 * their plan regardless), while Cooking Time / Budget / Cuisine are soft
 * ranking factors that surface closer matches first without excluding
 * anything.
 *
 * Screen 7 value formats, confirmed against FoodPreferencesStep.constants.js:
 *   - dietType: one of DIET_TYPE_OPTIONS ids — "vegetarian",
 *     "non_vegetarian", "eggetarian", "vegan", "jain".
 *   - cookingTime: one of COOKING_TIME_OPTIONS ids — "under_15", "15_30",
 *     "30_60", "60_plus" (or "" if unanswered).
 *   - budget: one of BUDGET_LEVELS ids — "budget", "moderate", "premium".
 *   - cuisines: array of CUISINE_OPTIONS ids.
 * Every catalog item in DislikesStep.constants.js's FOOD_SEARCH_ITEMS
 * already carries matching compatibleDietTypes / cookingTimeBand /
 * budgetCategory / cuisineTags fields precomputed against those exact
 * ids — this module compares against those fields directly. Every
 * resolver below fails OPEN (skips that filter) on anything
 * unrecognized, as a safety net against a stale/legacy `foodPreferences`
 * shape.
 *
 * File naming note: this file was previously named
 * DislikesStep_foodFilters.js (underscore) and imported that way from
 * DislikesStep.js, inconsistent with the dot convention every other file
 * in this screen's folder uses (DislikesStep.constants.js,
 * DislikesStep.styles.js, DislikesStep.validation.js). Renamed here to
 * DislikesStep.foodFilters.js to match — update the import in
 * DislikesStep.js accordingly if you're placing this by hand.
 */

const normalizeToken = (value) => String(value ?? "").toLowerCase().trim();

// ─── Diet Type (hard filter) ─────────────────────────────────────────────

const VALID_DIET_TYPES = ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"];

const resolveDietTypeToken = (rawDietType) => {
  const key = normalizeToken(rawDietType);
  return VALID_DIET_TYPES.includes(key) ? key : null;
};

const isDietCompatible = (item, dietTypeToken) => {
  if (!Array.isArray(item.compatibleDietTypes)) return true; // no metadata to check — fail open
  return item.compatibleDietTypes.includes(dietTypeToken);
};

// ─── Cooking Time (soft ranking factor — FSD v2.1 §6) ────────────────────

const COOKING_TIME_BAND_ORDER = ["under_15", "15_30", "30_60", "60_plus"];

const resolveCookingTimeToken = (rawCookingTime) => {
  const key = normalizeToken(rawCookingTime);
  return COOKING_TIME_BAND_ORDER.includes(key) ? key : null;
};

const cookingTimeMatchScore = (item, cookingTimeToken) => {
  if (!cookingTimeToken || !item.cookingTimeBand) return 0;
  const itemRank = COOKING_TIME_BAND_ORDER.indexOf(item.cookingTimeBand);
  const selectedRank = COOKING_TIME_BAND_ORDER.indexOf(cookingTimeToken);
  if (itemRank === -1) return 0;
  return itemRank <= selectedRank ? 1 : 0;
};

// ─── Budget (soft ranking factor — FSD v2.1 §6) ───────────────────────────

const BUDGET_ORDER = ["budget", "moderate", "premium"];

const resolveBudgetToken = (rawBudget) => {
  const key = normalizeToken(rawBudget);
  return BUDGET_ORDER.includes(key) ? key : null;
};

const budgetMatchScore = (item, budgetToken) => {
  if (!budgetToken || !item.budgetCategory) return 0;
  const itemRank = BUDGET_ORDER.indexOf(item.budgetCategory);
  const selectedRank = BUDGET_ORDER.indexOf(budgetToken);
  if (itemRank === -1) return 0;
  return itemRank <= selectedRank ? 1 : 0;
};

// ─── Cuisine (soft preference — ranking only) ────────────────────────────

const VALID_CUISINES = [
  "north_indian", "south_indian", "gujarati", "punjabi",
  "bengali", "rajasthani", "continental", "chinese",
];

const normalizeCuisineList = (rawCuisines) => {
  if (!Array.isArray(rawCuisines)) return [];
  return rawCuisines.map(normalizeToken).filter((c) => VALID_CUISINES.includes(c));
};

const cuisineMatchScore = (item, cuisineTokens) => {
  if (cuisineTokens.length === 0 || !Array.isArray(item.cuisineTags)) return 0;
  return item.cuisineTags.filter((tag) => cuisineTokens.includes(tag)).length;
};

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * @param {Array}  items            - FOOD_SEARCH_ITEMS (or a pre-filtered
 *   subset).
 * @param {object} [foodPreferences] - Screen 7 answers:
 *   { dietType, cuisines, cookingTime, budget }. Any/all fields may be
 *   missing (e.g. before Screen 7 is reached, or if a field was skipped).
 * @returns {Array} Diet-Type-compatible items (hard filter), sorted with
 *   Cooking-Time/Budget/Cuisine matches ranked first (soft factors — none
 *   of the three ever excludes an item). Never throws on unrecognized or
 *   missing preference values — it just filters/ranks less.
 */
export function filterAndRankFoodItems(items, foodPreferences) {
  const dietToken = resolveDietTypeToken(foodPreferences?.dietType);
  const cookingTimeToken = resolveCookingTimeToken(foodPreferences?.cookingTime);
  const budgetToken = resolveBudgetToken(foodPreferences?.budget);
  const cuisineTokens = normalizeCuisineList(foodPreferences?.cuisines);

  const filtered = dietToken
    ? items.filter((item) => isDietCompatible(item, dietToken))
    : items.slice();

  const hasRankingSignal = cookingTimeToken || budgetToken || cuisineTokens.length > 0;
  if (!hasRankingSignal) return filtered;

  return filtered
    .map((item) => ({
      item,
      score:
        cookingTimeMatchScore(item, cookingTimeToken) +
        budgetMatchScore(item, budgetToken) +
        cuisineMatchScore(item, cuisineTokens),
    }))
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

/**
 * Short, human-readable summary of which Screen 7 answers are currently
 * shaping the Dislikes suggestions, for an optional UI hint. Returns null
 * if no recognizable preferences were supplied.
 */
export function describeActiveFilters(foodPreferences) {
  if (!foodPreferences) return null;
  const parts = [];

  if (foodPreferences.dietType) parts.push(`${foodPreferences.dietType} diet`);
  if (foodPreferences.cookingTime) parts.push(`${foodPreferences.cookingTime} cooking time`);
  if (foodPreferences.budget) parts.push(`${foodPreferences.budget} budget`);
  if (Array.isArray(foodPreferences.cuisines) && foodPreferences.cuisines.length > 0) {
    parts.push(`prioritizing ${foodPreferences.cuisines.join(", ")}`);
  }

  return parts.length > 0 ? `Personalized to your ${parts.join(" · ")}` : null;
}